import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '../../components/SEO';
import SitePage, { CallToAction, styles, T, ACCENT } from '../../components/site/SitePage';
import { CANAUX, MARGE_EXEMPLE, marges } from './margeCalcul';
import './outils.css';

const PATH = '/outils/calcul-marge-amazon';
// Date de dernière mise à jour du contenu : visible sur la page et lue par Google (dateModified).
// À changer à chaque modification réelle du calcul ou du texte.
const MAJ = '2026-10-08';
const majFr = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(`${MAJ}T12:00:00`));

const RED = '#C2410C';
const eur = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(v);
const pc = (v) => `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1 }).format(v * 100)} %`;

const EX = marges(MARGE_EXEMPLE);

const FAQ = [
  ['La commission Amazon se calcule-t-elle sur le prix TTC ?', "Oui. En Europe, la commission de vente d'Amazon s'applique au prix total payé par le client, TVA comprise. Le calculateur l'applique donc au prix TTC, puis compte tout le reste hors taxes."],
  ['Et la publicité, sur quelle base ?', "Sur les ventes TTC, comme dans Seller Central et la console publicitaire d'Amazon, qui affichent des ventes TVA comprise. Un TACoS de 10 % y signifie 10 € de pub pour 100 € de ventes TTC. Le compter sur le hors taxes ferait paraître la pub 20 % moins chère qu'elle n'est."],
  ['Pourquoi compter les frais hors taxes ?', "Pour une entreprise assujettie à la TVA, la TVA facturée par Amazon, Shopify ou le transporteur sur leurs frais se récupère. Le vrai coût d'un frais, c'est son montant hors taxes."],
  ['ACoS ou TACoS : lequel indiquer ?', "Le TACoS. L'ACoS rapporte la publicité aux seules ventes qu'elle a déclenchées ; le TACoS la rapporte à toutes vos ventes du produit. Pour savoir ce que la pub coûte en moyenne sur chaque vente, c'est le TACoS qui compte."],
  ["Que mettre dans le coût d'un retour ?", "Ce qu'un retour vous coûte vraiment : les frais d'expédition non remboursés, les frais de remboursement d'Amazon, et la part des produits revenus invendables. Le calculateur le rapporte aux ventes conservées : avec 25 % de retours, chaque vente gardée en porte un tiers."],
  ['Où trouver mes frais FBA ?', "Dans Seller Central, avec l'aperçu des frais de vos produits, ou avec le simulateur de revenus d'Amazon. Ils dépendent de la taille et du poids du colis, et changent en général chaque année."],
  ['Pourquoi ma comptabilité donne-t-elle une autre marge ?', "Ce calcul donne une marge de contribution, unitaire et moyenne, avant frais fixes. Votre comptabilité voit aussi ce qui ne tombe pas sur une vente précise : abonnements, stockage de longue durée, retraits de stock, remboursements, écarts d'inventaire. Rapprocher les deux, produit par produit, c'est le travail d'un outil de marge."]
];

const FAQ_LD = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ.map(([q, a]) => ({ '@type': 'Question', name: q, acceptedAnswer: { '@type': 'Answer', text: a } }))
};

const APP_LD = {
  '@context': 'https://schema.org',
  '@type': 'WebApplication',
  name: 'Calculateur de marge Amazon, Shopify et B2B',
  url: `https://autoria.dev${PATH}`,
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  inLanguage: 'fr-FR',
  isAccessibleForFree: true,
  dateModified: MAJ,
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
  author: { '@type': 'Person', name: 'Mattéo', url: 'https://autoria.dev/about' }
};

const card = { background: '#FFFFFF', border: `1px solid ${T.line2}`, borderRadius: 10 };
const hint = { margin: 0, fontSize: 13, color: T.muted, lineHeight: 1.45 };
const COLORS = { produit: '#C9CED6', frais: '#9AA6C8', retours: '#D3D9EA', pub: '#F2C6AE', marge: ACCENT };

const Field = ({ id, label, value, onChange, unit, help }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
    <label htmlFor={id} style={{ fontSize: 14, fontWeight: 600 }}>{label}</label>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input id={id} className="ou-input" inputMode="decimal" autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} />
      {unit && <span style={{ fontFamily: T.mono, fontSize: 13, color: T.faint, whiteSpace: 'nowrap', minWidth: 22 }}>{unit}</span>}
    </div>
    {help && <p style={hint}>{help}</p>}
  </div>
);

// Décomposition du prix hors taxes : ce que coûte le produit, le canal, les retours, la pub, ce qui reste.
const Barre = ({ r }) => {
  const parts = [
    ['Produit', r.produit, COLORS.produit],
    ['Frais du canal', r.fraisCanal, COLORS.frais],
    ['Retours', r.retours, COLORS.retours],
    ['Publicité', r.pub, COLORS.pub],
    ['Marge', Math.max(0, r.marge), COLORS.marge]
  ].filter(([, v, ], i) => i < 3 || v > 0 || i === 4);
  const total = Math.max(r.prixHT, parts.slice(0, -1).reduce((a, [, v]) => a + v, 0));
  return (
    <div>
      <div role="img" aria-label={`Prix hors taxes de ${eur(r.prixHT)} : produit ${eur(r.produit)}, frais du canal ${eur(r.fraisCanal)}, retours ${eur(r.retours)}, publicité ${eur(r.pub)}, marge ${eur(r.marge)}`} style={{ display: 'flex', height: 14, borderRadius: 3, overflow: 'hidden', background: '#F1F2F4' }}>
        {parts.map(([label, v, color]) => <span key={label} style={{ width: `${(v / total) * 100}%`, background: color }} />)}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', marginTop: 8, fontSize: 12, color: T.muted }}>
        {parts.map(([label, , color]) => (
          <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><span style={{ width: 9, height: 9, background: color, borderRadius: 2 }} />{label}</span>
        ))}
      </div>
    </div>
  );
};

const Ligne = ({ label, value, strong }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0', borderTop: strong ? `1px solid ${T.line2}` : 0, fontWeight: strong ? 700 : 400, fontSize: 15 }}>
    <span style={{ color: strong ? T.ink : T.text }}>{strong ? label : `− ${label}`}</span>
    <span style={{ fontFamily: T.mono, fontVariantNumeric: 'tabular-nums', color: value < 0 ? RED : T.ink }}>{eur(value)}</span>
  </div>
);

const Resultat = ({ r }) => {
  if (r.error) return <p style={{ margin: 0, color: T.muted }}>{r.error}</p>;
  const perte = r.marge < 0;
  let verdict;
  if (r.avantPub <= 0) verdict = <>Même sans publicité, chaque vente perd <strong>{eur(-r.avantPub)}</strong> : seuls le prix, l'achat ou la logistique peuvent changer la donne.</>;
  else if (r.pubMax === null) verdict = <>Pas de publicité sur ce canal : la marge avant frais fixes est de <strong>{pc(r.taux)}</strong> du prix.</>;
  else if (perte) verdict = <>Chaque vente vous coûte <strong>{eur(-r.marge)}</strong>. Pour revenir à l'équilibre, la publicité doit descendre sous <strong>{pc(r.pubMax)}</strong> des ventes TTC, ou le prix monter.</>;
  else verdict = <>Au-delà de <strong>{pc(r.pubMax)}</strong> de publicité (en part des ventes TTC), ce produit vend à perte sur ce canal.</>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <p style={{ margin: 0, fontFamily: T.mono, fontSize: 12, color: T.faint }}>Marge de contribution par vente, avant frais fixes</p>
        <p style={{ margin: 0, fontSize: 34, fontWeight: 800, fontStretch: '80%', lineHeight: 1.05, color: perte ? RED : T.ink }}>{eur(r.marge)} <span style={{ fontSize: 15, fontWeight: 600, color: T.muted }}>{pc(r.taux)} du prix HT</span></p>
      </div>
      <Barre r={r} />
      <div>
        <Ligne label="Prix de vente hors taxes" value={r.prixHT} strong />
        <Ligne label="Produit (achat et transport)" value={r.produit} />
        {r.frais.map(([label, v]) => <Ligne key={label} label={label} value={v} />)}
        <Ligne label="Retours" value={r.retours} />
        <Ligne label="Marge avant publicité" value={r.avantPub} strong />
        {r.pubMax !== null && <Ligne label="Publicité" value={r.pub} />}
        {r.pubMax !== null && <Ligne label="Marge après publicité" value={r.marge} strong />}
      </div>
      <p style={{ margin: 0, fontSize: 14, color: T.text, lineHeight: 1.5 }}>{verdict}</p>
    </div>
  );
};

const Calculateur = () => {
  const [p, setP] = useState(MARGE_EXEMPLE);
  const m = useMemo(() => marges(p), [p]);
  const used = useRef(false);
  const touched = () => {
    if (used.current || !window.autoriaTrack) return;
    used.current = true;
    window.autoriaTrack('calculateur_marge_utilise');
  };
  const set = (key) => (v) => { touched(); setP((prev) => ({ ...prev, [key]: v })); };
  const setIn = (canal, key) => (v) => { touched(); setP((prev) => ({ ...prev, [canal]: { ...prev[canal], [key]: v } })); };

  const classement = CANAUX.filter(([k]) => !m[k].error).map(([k, nom]) => [nom, m[k].marge]).sort((a, b) => b[1] - a[1]);
  const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(140px, 100%), 1fr))', gap: 14 };
  const titre = { margin: 0, fontSize: 20, fontWeight: 800, fontStretch: '88%' };
  const carte = { ...card, flex: '1 1 340px', minWidth: 0, padding: 'clamp(16px, 3vw, 24px)', display: 'flex', flexDirection: 'column', gap: 16 };
  const bas = { marginTop: 'auto', paddingTop: 16, borderTop: `1px solid ${T.line}` };

  return (
    <div style={{ ...styles.container, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <section aria-labelledby="mg-produit" style={{ ...card, padding: 'clamp(16px, 3vw, 24px)', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <h2 id="mg-produit" style={titre}>1 · Votre produit</h2>
        <div style={grid}>
          <Field id="mg-achat" label="Prix d'achat" unit="€ HT" value={p.achat} onChange={set('achat')} help="Par unité, chez le fournisseur." />
          <Field id="mg-transport" label="Transport jusqu'à vous" unit="€ HT" value={p.transportIn} onChange={set('transportIn')} help="Fret, droits de douane, transitaire, par unité (hors TVA à l'import, récupérable)." />
          <Field id="mg-tva" label="TVA sur vos ventes" unit="%" value={p.tva} onChange={set('tva')} />
        </div>
      </section>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'stretch' }}>
        <section aria-labelledby="mg-amazon" style={carte}>
          <h2 id="mg-amazon" style={titre}>2 · Une vente sur Amazon (FBA)</h2>
          <div style={grid}>
            <Field id="mg-a-prix" label="Prix de vente" unit="€ TTC" value={p.amazon.prix} onChange={setIn('amazon', 'prix')} />
            <Field id="mg-a-rem" label="Remise moyenne" unit="%" value={p.amazon.remise} onChange={setIn('amazon', 'remise')} help="Coupons, offres éclair, Prime Day." />
            <Field id="mg-a-com" label="Commission" unit="%" value={p.amazon.commission} onChange={setIn('amazon', 'commission')} help="15 % dans la plupart des catégories." />
            <Field id="mg-a-fba" label="Frais FBA" unit="€ HT" value={p.amazon.fba} onChange={setIn('amazon', 'fba')} help="Expédition par Amazon, par unité." />
            <Field id="mg-a-envoi" label="Envoi vers Amazon" unit="€ HT" value={p.amazon.envoi} onChange={setIn('amazon', 'envoi')} help="Transport et étiquetage jusqu'à leurs entrepôts, par unité." />
            <Field id="mg-a-stock" label="Stockage par mois" unit="€ HT" value={p.amazon.stockage} onChange={setIn('amazon', 'stockage')} help="Par unité. Plus cher d'octobre à décembre." />
            <Field id="mg-a-mois" label="Mois en stock" unit="mois" value={p.amazon.mois} onChange={setIn('amazon', 'mois')} help="Avant d'être vendue, en moyenne." />
            <Field id="mg-a-pub" label="Publicité (TACoS)" unit="%" value={p.amazon.pub} onChange={setIn('amazon', 'pub')} help="Dépense pub ÷ ventes TTC, comme dans Seller Central." />
            <Field id="mg-a-ret" label="Taux de retour" unit="%" value={p.amazon.retours} onChange={setIn('amazon', 'retours')} />
            <Field id="mg-a-cret" label="Coût d'un retour" unit="€ HT" value={p.amazon.coutRetour} onChange={setIn('amazon', 'coutRetour')} help="Frais non remboursés, et produit parfois invendable." />
          </div>
          <div style={bas}><Resultat r={m.amazon} /></div>
        </section>

        <section aria-labelledby="mg-site" style={carte}>
          <h2 id="mg-site" style={titre}>3 · Une vente sur votre site</h2>
          <div style={grid}>
            <Field id="mg-s-prix" label="Prix de vente" unit="€ TTC" value={p.site.prix} onChange={setIn('site', 'prix')} />
            <Field id="mg-s-rem" label="Remise moyenne" unit="%" value={p.site.remise} onChange={setIn('site', 'remise')} help="Codes promo, soldes." />
            <Field id="mg-s-pay" label="Frais de paiement" unit="%" value={p.site.paiementPct} onChange={setIn('site', 'paiementPct')} help="Shopify Payments, Stripe, PayPal…" />
            <Field id="mg-s-payfix" label="Frais fixes par commande" unit="€ HT" value={p.site.paiementFixe} onChange={setIn('site', 'paiementFixe')} />
            <Field id="mg-s-unites" label="Unités par commande" unit="" value={p.site.unites} onChange={setIn('site', 'unites')} help="Pour répartir les frais par commande." />
            <Field id="mg-s-exp" label="Expédition à votre charge" unit="€ HT" value={p.site.expedition} onChange={setIn('site', 'expedition')} help="Par commande, moins ce que paie le client, hors TVA." />
            <Field id="mg-s-prep" label="Préparation" unit="€ HT" value={p.site.preparation} onChange={setIn('site', 'preparation')} help="Par commande, logisticien ou temps passé." />
            <Field id="mg-s-stock" label="Stockage" unit="€ HT" value={p.site.stockage} onChange={setIn('site', 'stockage')} help="Par unité vendue, chez vous ou au logisticien." />
            <Field id="mg-s-pub" label="Publicité (part du CA)" unit="%" value={p.site.pub} onChange={setIn('site', 'pub')} help="Dépense pub ÷ ventes TTC du site." />
            <Field id="mg-s-ret" label="Taux de retour" unit="%" value={p.site.retours} onChange={setIn('site', 'retours')} />
            <Field id="mg-s-cret" label="Coût d'un retour" unit="€ HT" value={p.site.coutRetour} onChange={setIn('site', 'coutRetour')} />
          </div>
          <div style={bas}><Resultat r={m.site} /></div>
        </section>

        <section aria-labelledby="mg-b2b" style={carte}>
          <h2 id="mg-b2b" style={titre}>4 · Une vente à un revendeur</h2>
          <div style={grid}>
            <Field id="mg-b-prix" label="Prix revendeur" unit="€ HT" value={p.b2b.prix} onChange={setIn('b2b', 'prix')} help="Votre tarif professionnel." />
            <Field id="mg-b-rem" label="Remises et ristournes" unit="%" value={p.b2b.remise} onChange={setIn('b2b', 'remise')} help="Remise de fin d'année, escompte." />
            <Field id="mg-b-com" label="Commission d'agent" unit="%" value={p.b2b.commission} onChange={setIn('b2b', 'commission')} help="0 si vous vendez en direct." />
            <Field id="mg-b-tr" label="Transport" unit="€ HT" value={p.b2b.transport} onChange={setIn('b2b', 'transport')} help="Franco de port, ramené à l'unité." />
            <Field id="mg-b-ret" label="Retours et invendus" unit="%" value={p.b2b.retours} onChange={setIn('b2b', 'retours')} />
            <Field id="mg-b-cret" label="Coût d'un retour" unit="€ HT" value={p.b2b.coutRetour} onChange={setIn('b2b', 'coutRetour')} />
          </div>
          <div style={bas}><Resultat r={m.b2b} /></div>
        </section>
      </div>

      {classement.length > 1 && (
        <div role="status" style={{ borderLeft: `4px solid ${ACCENT}`, background: '#FFFFFF', padding: '14px 16px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <p style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
            Par vente, le plus rentable : {classement.map(([nom, v], i) => `${i === 0 ? '' : i === classement.length - 1 ? ', puis ' : ', '}${nom} (${eur(v)})`).join('')}.
          </p>
          <button type="button" className="ou-link" style={{ fontSize: 15 }} onClick={() => setP(MARGE_EXEMPLE)}>Revenir à l'exemple</button>
        </div>
      )}
    </div>
  );
};

const H2 = ({ children, id }) => <h2 id={id} style={{ ...styles.h2, marginTop: 56 }}>{children}</h2>;
const P = ({ children }) => <p style={{ margin: '16px 0 0', fontSize: 18, color: T.text, lineHeight: 1.6 }}>{children}</p>;

const CalculMargePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const a = EX.amazon;
  return (
    <SitePage>
      <SEO
        title="Calculateur de marge Amazon, Shopify et B2B (gratuit)"
        description="Calculez votre marge par vente sur Amazon, sur votre site et en B2B : commission, frais FBA, remises, publicité et retours déduits. Gratuit, sans inscription."
        path={PATH}
        breadcrumb={[['Outils gratuits', '/outils'], ['Calculateur de marge', PATH]]}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(FAQ_LD)}</script>
        <script type="application/ld+json">{JSON.stringify(APP_LD)}</script>
      </Helmet>

      <section style={{ ...styles.container, padding: '56px 24px 32px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <p style={styles.kicker}>Outil gratuit · sans inscription · mis à jour le {majFr}</p>
        <h1 style={{ ...styles.h1, maxWidth: 900 }}>Calculateur de marge Amazon, Shopify et B2B : ce qui vous reste vraiment</h1>
        <p style={{ ...styles.lead, maxWidth: 720 }}>Le même produit, trois canaux : combien vous rapporte une vente sur Amazon, sur votre site et à un revendeur, une fois la commission, la logistique, les remises, les retours et la publicité payés.</p>
      </section>

      <Calculateur />

      <article style={{ ...styles.narrow, paddingTop: 24 }}>
        <H2>Comment se calcule la marge réelle sur Amazon</H2>
        <P>On part du prix hors taxes, puis on retire tout ce qu'une vente coûte vraiment :</P>
        <div style={{ marginTop: 20, padding: '18px 20px', background: '#FFFFFF', border: `1px solid ${T.line2}`, borderLeft: `4px solid ${ACCENT}`, fontFamily: T.mono, fontSize: 'clamp(14px, 2vw, 17px)', lineHeight: 1.7 }}>
          Marge = prix HT − produit − commission − frais FBA − envoi − stockage − retours − publicité
        </div>
        <P>Dans l'exemple, la Veste Altitude de la démo, vendue {eur(a.prixTTC)} TTC sur Amazon : il reste {eur(a.prixHT)} hors taxes. Après le produit, la commission, l'expédition FBA, l'envoi vers Amazon, le stockage et les retours, la marge avant publicité est de {eur(a.avantPub)}. Avec {pc(a.pubTaux)} de publicité sur les ventes TTC, il en reste {eur(a.marge)}, soit {pc(a.taux)} du prix hors taxes.</P>
        <P>Les deux pièges les plus courants : la commission et la publicité se lisent sur le prix TTC, alors que la marge se lit hors taxes. Sur un produit à 40 € TTC, une commission de 15 % comptée sur le hors taxes paraît un euro moins chère qu'elle n'est. Et un TACoS compté sur le hors taxes fait paraître la pub 20 % moins chère.</P>

        <H2>Amazon, votre site ou le B2B : pourquoi la marge diffère</H2>
        <P>Sur Amazon, la commission et la logistique FBA pèsent lourd, mais la publicité coûte souvent moins cher, parce que le trafic est déjà là. Sur votre site, les frais de paiement sont faibles, mais vous payez l'expédition, la préparation, et souvent plus de publicité pour faire venir chaque client. En B2B, le prix est plus bas, mais il n'y a ni publicité ni frais de plateforme.</P>
        <P>Aucun canal n'est meilleur dans l'absolu : tout dépend du produit. Un article lourd et peu cher perd souvent de l'argent sur Amazon, un best-seller léger y gagne. Faire ce calcul produit par produit, c'est savoir où pousser la publicité, quoi réserver aux revendeurs, et quoi retirer d'un canal.</P>

        <H2>Ce que ce calcul ne fait pas</H2>
        <P>Il donne une marge de contribution, par vente moyenne, avant frais fixes. Il ne voit ni les abonnements, ni le stockage de longue durée, ni les retraits de stock, ni les écarts entre ce qu'Amazon annonce et ce qu'il verse vraiment.</P>
        <P>Sur trois produits, un tableur suffit. Sur tout un catalogue, et chaque mois, avec des chiffres qui collent à la comptabilité, c'est le rôle d'<Link to="/marge-par-canal">un outil de marge sur mesure</Link> : la marge de chaque produit, sur chaque canal, rapprochée de vos virements. Pour le stock, voyez aussi le <Link to="/outils/calcul-stock-de-securite">calculateur de stock de sécurité</Link>.</P>

        <H2 id="faq">Questions fréquentes</H2>
        <div style={{ marginTop: 20, borderTop: `1px solid ${T.line2}` }}>
          {FAQ.map(([q, a2]) => (
            <details key={q} style={{ borderBottom: `1px solid ${T.line2}`, padding: '16px 0' }}>
              <summary style={{ cursor: 'pointer', fontSize: 18, fontWeight: 700 }}>{q}</summary>
              <p style={{ margin: '10px 0 0', fontSize: 17, color: T.text, lineHeight: 1.6 }}>{a2}</p>
            </details>
          ))}
        </div>
      </article>

      <CallToAction from="calculateur-marge" title="Votre marge réelle, produit par produit ?" text="30 minutes pour voir si un outil de marge sur mesure vaut le coup chez vous. Si oui, un prix fixe par écrit sous 3 jours." />
    </SitePage>
  );
};

export default CalculMargePage;
